import React, { useState } from 'react';
import { AutoComplete, Button, Space, Spin, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { DefaultOptionType } from 'antd/es/select';
import { useTranslation } from 'react-i18next';
import { useCustomerOptions } from '../../modules/reference-data/queries/useCustomerOptions';
import { CustomerOptionDto } from '../../dto/reference';
import CustomerPickerModal from '../customers/CustomerPickerModal';

const { Text } = Typography;

interface CustomerNameOption extends DefaultOptionType {
  customer: CustomerOptionDto;
}

interface CustomerNameComboboxProps {
  /** The document's current recipient name — this IS the input's value, so a
   *  loaded or freely-typed recipient name always displays natively (no
   *  separate id lookup). */
  value?: string;
  /** Fires on every text change (typing, or the transient value rc-select
   *  emits when an option is picked). Always set the recipient name from
   *  this — a subsequent onSelectCustomer call (for an actual catalog pick)
   *  supersedes it in the same event with the resolved customer fields. */
  onChange?: (name: string) => void;
  /** Fires only when the user explicitly picks a customer suggestion. */
  onSelectCustomer?: (customer: CustomerOptionDto) => void;
  placeholder?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Recipient name field for WZ documents: a free-text combobox whose value IS
 * the document's recipientName (so legacy or manually-entered recipients not
 * in the customer list always show their saved name), with catalog
 * suggestions from the customer list (name + NIP/address secondary line for
 * disambiguation, mirroring ProductNameCombobox's layout). Picking a
 * suggestion autofills the rest of the recipient block via
 * `onSelectCustomer`; typing free text just updates the name — recipientName
 * has always been a plain text field on WZDocument, so no customer link is
 * persisted.
 */
const CustomerNameCombobox: React.FC<CustomerNameComboboxProps> = ({
  value,
  onChange,
  onSelectCustomer,
  placeholder,
  disabled = false,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation(['customers']);
  // Skip the fetch while the field is disabled (read-only document view) —
  // matches the previous standalone "Klient (odbiorca)" picker's canEdit-gated load.
  const { options: customers, loading, refresh } = useCustomerOptions(!disabled);
  const [pickerOpen, setPickerOpen] = useState(false);

  const options: CustomerNameOption[] = customers.map((customer) => {
    const secondary = [
      customer.taxId ? `NIP ${customer.taxId}` : null,
      customer.address,
    ].filter(Boolean).join(' · ');

    return {
      value: customer.id,
      customer,
      label: (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <Text strong style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {customer.name}
          </Text>
          {secondary && (
            <Text type="secondary" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {secondary}
            </Text>
          )}
        </Space>
      ),
    };
  });

  const handleSelect = (_value: string, option: CustomerNameOption) => {
    onSelectCustomer?.(option.customer);
  };

  // Mirrors an AutoComplete pick exactly: the resolved customer feeds the
  // side-channel callback, and its name becomes the field's text value, same
  // as onChange(text) does when a suggestion is chosen from the dropdown.
  const handlePickerSelect = (customer: CustomerOptionDto) => {
    onSelectCustomer?.(customer);
    onChange?.(customer.name);
    setPickerOpen(false);
  };

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <AutoComplete
          value={value}
          options={options}
          onChange={(text) => onChange?.(text || '')}
          onSelect={handleSelect}
          filterOption={(input, option) => {
            const customer = (option as CustomerNameOption | undefined)?.customer;
            if (!customer) return false;
            return customer.searchText.includes(input.toLowerCase());
          }}
          disabled={disabled}
          placeholder={placeholder}
          style={{ width: '100%' }}
          data-testid={dataTestId}
          notFoundContent={loading ? <Spin size="small" /> : null}
          popupMatchSelectWidth={false}
        />
        <Button
          icon={<SearchOutlined />}
          disabled={disabled}
          loading={loading && customers.length === 0}
          onClick={() => setPickerOpen(true)}
          aria-label={t('customers:customerPicker.openButton')}
        >
          {t('customers:customerPicker.browse')}
        </Button>
      </Space.Compact>

      <CustomerPickerModal
        open={pickerOpen}
        customers={customers}
        loading={loading}
        onRefresh={refresh}
        onSelect={handlePickerSelect}
        onCancel={() => setPickerOpen(false)}
      />
    </>
  );
};

export default CustomerNameCombobox;
